import React from "react";
import { useNavigate } from "react-router-dom";
import { User } from "lucide-react";
import { api } from "../../services/api";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import {
    Form,
    FormField,
    FormItem,
    FormControl,
    FormMessage,
} from "../../components/ui/form";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Separator } from "../../components/ui/separator";
import { toast } from "sonner";

export const ForgotPassword = () => {
    const { t } = useTranslation("auth");
    const navigate = useNavigate();

    const form = useForm({
        defaultValues: { email: "" },
    });

    const onSubmit = (data: { email: string }) => {
        api.post("/reset-password", data, {
            hideSuccessToast: true,
            hideErrorToast: true,
        })
            .then(() => {
                toast.success(t("forgotPassword.sendSuccess"));
                navigate(
                    `/reset-password?email=${encodeURIComponent(data.email)}`,
                );
            })
            .catch((error) => {
                if (error.response?.status === 401) {
                    toast.error(t("forgotPassword.emailNotFound"));
                } else if (!error.response) {
                    toast.error(
                        t(
                            "common:api.connectionError",
                            "Erro de conexão ou tempo de limite excedido. Verifique sua internet.",
                        ),
                    );
                } else {
                    toast.error(
                        t(
                            "common:api.unexpected",
                            "Ocorreu um erro inesperado ({{status}})",
                            { status: error.response.status },
                        ),
                    );
                }
            });
    };

    return (
        <div className="bg-background min-h-screen flex flex-col justify-center">
            <div className="flex flex-1" />
            <div className="flex flex-1 justify-center items-center w-full">
                <div className="w-full max-w-sm px-4">
                    <div className="border rounded-lg shadow-sm p-6 bg-card w-full">
                        <Form {...form}>
                            <form
                                onSubmit={form.handleSubmit(onSubmit)}
                                className="space-y-6"
                            >
                                <div className="flex flex-col items-center justify-center space-y-4">
                                    <img
                                        src={require("../../assets/dream-book-logo.png")}
                                        alt="Logo"
                                        className="max-w-[240px]"
                                    />
                                    <Separator />
                                    <h5 className="text-muted-foreground text-sm text-center">
                                        {t("forgotPassword.subtitle")}
                                    </h5>
                                </div>

                                <FormField
                                    control={form.control}
                                    name="email"
                                    rules={{ required: true }}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <div className="relative">
                                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-5 h-5" />
                                                    <Input
                                                        className="pl-10 h-10"
                                                        placeholder={t(
                                                            "forgotPassword.emailPlaceholder",
                                                        )}
                                                        {...field}
                                                    />
                                                </div>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <div className="flex flex-col space-y-2 mt-4">
                                    <Button type="submit" className="w-full">
                                        {t("forgotPassword.submitButton")}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="link"
                                        className="w-full text-primary"
                                        onClick={() => navigate("/signin")}
                                    >
                                        {t("forgotPassword.loginLink")}
                                    </Button>
                                </div>
                            </form>
                        </Form>
                    </div>
                </div>
            </div>
            <div className="flex flex-[2]" />
        </div>
    );
};
